import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';
import { ArchivedData } from '../models/archived-data.model';
import { ConsentToUpload } from '../models/consent-to-upload.model';
import {
  ConsentModel,
  ConsentUpdateStatusModel,
} from '../models/consent.model';
import { OrgDataModel } from '../models/orgData.model';
import { StudentProfile } from '../models/student-profile.model';
import { StudentModel } from '../models/student.model';
import { StudentRegistrationStatusCodes } from '../models/StudentFormStatusCodes.enum';
import { SettingsModel } from '../models/settings.model';
import { AddressValidationUploadModel } from '../models/address-validation-upload.model';

@Injectable({
  providedIn: 'root',
})
export class BackofficeService {
  backofficeController: string = '/api/Backoffice';
  public orgInfo: OrgDataModel;
  constructor(private http: HttpClient) {}

  public NamesEn = {
    FirstName: 'First Name',
    LastName: 'Last Name',
    DOB: 'Date of Birth',
    ProgramLocation: 'Program Location',
    EmailAddress: 'Email Address',
    CurrentSchool: 'Current School',
    Unit: 'Unit No',
    PhoneNumber: 'Phone Number',
    Address: 'Address',
    City: 'City',
    PostalCode: 'Postal Code',
    Province: 'Province',
    Gender: 'Gender',
    EthnicOrigin: 'Cultural Background',
    Indigenous: 'Indigenous',
    CountryOfBirth: 'Country of birth',
    DateOfArrivalToCanada: 'Date of arrival to Canada',
    PrimaryLang: 'Primary Language',
    SecondaryLang: 'Secondary Language',
    School: 'School',
    PreviousSchool: 'Previous School',
    Grade: 'Grade',
    Guardian1FirstName: "Guardian's First Name",
    Guardian1LastName: "Guardian's Last Name",
    Guardian1PhoneNumber: 'Guardian’s Phone Number',
    Guardian1Address: 'Guardian’s Address',
    Guardian1Email: 'Guardian’s Email address',
    Guardian1City: "Guardian's City",
    Guardian1PostalCode: "Guardian's Postal Code",
    Guardian1Province: "Guardian's Province",
    Guardian1TextConsent: "Guardian's Text Consent",
    Guardian1EmailConsent: "Guardian's Email Consent",
    Guardian1PrimaryLanguage: "Guardian's Primary Language",
    Guardian2FirstName: "Guardian's First Name",
    Guardian2LastName: "Guardian's Last Name",
    Guardian2PhoneNumber: 'Guardian’s Phone Number',
    Guardian2Address: 'Guardian’s Address',
    Guardian2Email: 'Guardian’s Email address',
    Guardian2City: "Guardian's City",
    Guardian2PostalCode: "Guardian's Postal Code",
    Guardian2Province: "Guardian's Province",
    Guardian2TextConsent: "Guardian's Text Consent",
    Guardian2EmailConsent: "Guardian's Email Consent",
    Guardian2PrimaryLanguage: "Guardian's Primary Language",
    ConsenttoConnect: 'Consent To Contact',
  };

  public NamesFr = {
    FirstName: 'Prénom',
    LastName: 'Nom de famille',
    DOB: 'Date de naissance',
    ProgramLocation: 'Emplacement de programme',
    EmailAddress: 'Adresse courriel',
    CurrentSchool: 'École actuellement fréquentée',
    Unit: 'Numéro d’unité',
    PhoneNumber: 'Numéro de téléphone',
    Address: 'Adresse',
    City: 'Ville',
    PostalCode: 'Code postal',
    Province: 'Province',
    Gender: 'Sexe',
    EthnicOrigin: 'Origine ethnique',
    Indigenous: 'Indigenous',
    CountryOfBirth: 'Pays de naissance',
    DateOfArrivalToCanada: "Date d'arrivée au Canada",
    PrimaryLang: 'Première langue',
    SecondaryLang: 'Deuxième langue',
    School: 'Établissement scolaire',
    PreviousSchool: 'Établissement scolaire antérieur',
    Grade: 'Niveau',
    Guardian1FirstName: 'Prénom du tuteur',
    Guardian1LastName: 'Nom de famille du tuteur',
    Guardian1PhoneNumber: 'Numéro de téléphone du tuteur',
    Guardian1Address: 'Adresse du tuteur',
    Guardian1Email: 'Adresse courriel du tuteur',
    Guardian1City: 'Ville du tuteur',
    Guardian1PostalCode: 'Code postal du tuteur',
    Guardian1Province: 'Province du tuteur',
    Guardian1TextConsent : "Consentement textuel du tuteur",
    Guardian1EmailConsent : "Consentement du tuteur par adresse courriel",
    Guardian1PrimaryLanguage : "Langue principale du tuteur",
    Guardian2FirstName: 'Prénom du tuteur',
    Guardian2LastName: 'Nom de famille du tuteur',
    Guardian2PhoneNumber: 'Numéro de téléphone du tuteur',
    Guardian2Address: 'Adresse du tuteur',
    Guardian2Email: 'Adresse courriel du tuteur',
    Guardian2City: 'Ville du tuteur',
    Guardian2PostalCode: 'Code postal du tuteur',
    Guardian2Province: 'Province du tuteur',
    Guardian2TextConsent : "Consentement textuel du tuteur",
    Guardian2EmailConsent : "Consentement du tuteur par adresse courriel",
    Guardian2PrimaryLanguage : "Langue principale du tuteur",
    ConsenttoConnect: 'Consentement à la communication',
  };

  getAllInbox(orgCode: string): Observable<StudentModel[]> {
    return this.http
      .get<StudentModel[]>(this.backofficeController + `/Inbox/${orgCode}`)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  getStudentProfile(studentId: number): Observable<StudentProfile> {
    return this.http
      .get<StudentProfile>(this.backofficeController + `/profile/${studentId}`)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  getConsentForms(): Observable<ConsentModel[]> {
    return this.http
      .get<ConsentModel[]>(
        this.backofficeController + `/consentforms`
      )
      .pipe(map((data) => data));
  }
  downloadConsentForm(consentId: number): any {
    return this.http
      .get(
        this.backofficeController + `/DownloadDocument?consentId=${consentId}`,
        {
          responseType: 'arraybuffer',
        }
      )
      .pipe(
        map((res) => {
          return new Blob([res], { type: 'application/pdf' });
        })
      );
  }

  downloadSignedConsentForm(fileId: string): any {
    return this.http
      .get(
        this.backofficeController + `/DownloadSignedDocument?fileId=${fileId}`,
        { responseType: 'arraybuffer' }
      )
      .pipe(
        map((res) => {
          return new Blob([res], { type: 'application/pdf' });
        })
      );
  }

  downloadAddressValidationPhoto(
    studentRegistrationSchoolYearId: number
  ): Observable<unknown> {
    return this.http
      .get(
        `${this.backofficeController}/downloadaddressphoto/${studentRegistrationSchoolYearId}`,
        { responseType: 'arraybuffer' })
      .pipe(
        map((data: any) => {
          return new Blob([data]);
        })
      );
  }

  uploadAddressValidationPhoto(
    upload: AddressValidationUploadModel
  ): Observable<AddressValidationUploadModel> {
    return this.http
      .post<AddressValidationUploadModel>(
        `${this.backofficeController}/UploadAddressPhoto`,
        upload
      )
      .pipe(map((data) => data));
  }

  saveConsentForm(consentForm: ConsentToUpload): Observable<Object> {
    return this.http
      .post<Object>(this.backofficeController + `/uploaddocument`, consentForm)
      .pipe(map((data) => data));
  }

  updateConsentStatus(consent: ConsentUpdateStatusModel): Observable<Object> {
    return this.http
      .post<Object>(this.backofficeController + '/UpdateConsentStatus', consent)
      .pipe(map((data) => data));
  }

  getAllSyncFailed(orgCode: string): Observable<StudentModel[]> {
    return this.http
      .get<StudentModel[]>(this.backofficeController + `/syncfailed/${orgCode}`)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  getArchived(orgCode: string): Observable<StudentModel[]> {
    return this.http
      .get<StudentModel[]>(this.backofficeController + `/archived/${orgCode}`)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }
  getSynced(orgCode: string): Observable<StudentModel[]> {
    return this.http
      .get<StudentModel[]>(this.backofficeController + `/synced/${orgCode}`)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }
  getSyncing(orgCode: string): Observable<StudentModel[]> {
    return this.http
      .get<StudentModel[]>(this.backofficeController + `/syncing/${orgCode}`)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }
  setSynced(
    studentIds: number[],
    schoolYearId?: number
  ): Observable<ArchivedData[]> {
    return this.http
      .post<ArchivedData[]>(
        this.backofficeController +
          `/UpdateSubmissionStatus/${StudentRegistrationStatusCodes.Reviewed}`,
        { studentIds, schoolYearId }
      )
      .pipe(
        map((data) => {
          return data;
        })
      );
  }
  // https://pam-test-app-service.azurewebsites.net/api/ TODO: DO NOT LOSE
  setArchived(
    studentIds: number[],
    schoolYearId?: number
  ): Observable<ArchivedData[]> {
    return this.http
      .post<ArchivedData[]>(
        this.backofficeController +
          `/UpdateSubmissionStatus/${StudentRegistrationStatusCodes.Archived}`,
        { studentIds, schoolYearId }
      )
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  setDeleted(studentIds: number[]): Observable<ArchivedData[]> {
    return this.http
      .post<ArchivedData[]>(
        this.backofficeController +
          `/UpdateSubmissionStatus/${StudentRegistrationStatusCodes.Deleted}`,
        { studentIds, schoolYearId: null }
      )
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  sendSubmissionToInbox(
    studentIds: number[],
    newStatus: number
  ): Observable<StudentModel> {
    return this.http
      .post<StudentModel>(
        this.backofficeController + `/UpdateSubmissionStatus/${newStatus}`,
        { studentIds, schoolYearId: null }
      )
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  getOrgInfo(): Observable<OrgDataModel> {
    return this.http
      .get<OrgDataModel>(this.backofficeController + '/OrgInfo')
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  getStudentsExport(
    ids: number[],
    lang: string,
    fileType: string
  ): Observable<any> {
    var data = {
      ids: ids,
      names: lang === 'en' ? this.NamesEn : this.NamesFr,
      fileType: fileType,
    };
    return this.http.post<any>(
      `${this.backofficeController}/GetStudentExport`,
      data,
      { observe: 'response', responseType: 'blob' as 'json' }
    );
  }
}
